/**
 * The plan lifted under the marks, so a mark reads on a shaded plan as it does on white paper.
 *
 * Plans are often shaded — pavement a mid grey, a lot a light one — and a translucent colour
 * multiplied over grey comes out a muddy olive that can hardly be told from the shading round it.
 * So under every mark (its body, its colour band and a hair beyond, which stands in for a white
 * casing) the plan is shown lifted: each pixel divided by the paper tone around it (a flat-field
 * correction, as a document scanner evens out a page). Shading becomes paper white, while the
 * plan's ink keeps its darkness against it — lines, text and symbols read as before — and the
 * mark's colour then lies over it exactly as over white paper. Where the plan is white already
 * nothing changes at all.
 *
 * The paper tone is the plan's lightness closed (a maximum then a minimum filter, wider than any
 * line or letter): ink narrower than the window is taken out and the shading's own edges stay
 * sharp. Tone darker than a mid grey is taken for ink (a filled symbol), not shading, and left be.
 * (The making is in liftcore.ts; here, keeping it for each picture of the plan, and the worker.)
 */
import { makeLifted } from './liftcore.ts';

/** The lifted plan, made once per picture of the plan (null: nothing to lift — a plan all paper and ink — or it cannot be read). */
const CACHE = new WeakMap<object, CanvasImageSource | null>();

/**
 * The lifted plan for a picture of the plan (null: nothing to lift). Made here and now if it is not
 * made yet — unless `sync` is false (the editor, which draws the marks straight on the plan until the
 * worker has it ready: `liftInWorker`, `useLift`), when it is `undefined` meanwhile.
 */
export function liftedPlan(image: CanvasImageSource, w: number, h: number, sync = true): CanvasImageSource | null | undefined {
  const key = image as unknown as object;
  if (CACHE.has(key)) return CACHE.get(key) ?? null;
  if (!sync) return undefined;
  let out: CanvasImageSource | null = null;
  try {
    out = makeLifted(image, w, h);
  } catch {
    out = null;
  }
  CACHE.set(key, out);
  return out;
}

/** The lifted plan made elsewhere (the worker) is the one for this picture of the plan. */
export function useLift(image: CanvasImageSource, lifted: CanvasImageSource | null): void {
  CACHE.set(image as unknown as object, lifted);
}

let worker: Worker | null = null;
let nextId = 1;
const waiting = new Map<number, (b: ImageBitmap | null) => void>();
/** The last plans lifted in the worker, by key (a map and its image): a map opened again has it at once. */
const BY_KEY = new Map<string, Promise<ImageBitmap | null>>();

/** The plan lifted off the main thread, from its file (null: nothing to lift, or no worker). */
export function liftInWorker(key: string, blob: Blob): Promise<ImageBitmap | null> {
  const known = BY_KEY.get(key);
  if (known) return known;
  const job = new Promise<ImageBitmap | null>((resolve) => {
    try {
      if (!worker) {
        worker = new Worker(new URL('./lift.worker.ts', import.meta.url), { type: 'module' });
        worker.onmessage = (e: MessageEvent<{ id: number; bitmap: ImageBitmap | null }>) => {
          waiting.get(e.data.id)?.(e.data.bitmap);
          waiting.delete(e.data.id);
        };
        worker.onerror = () => {
          for (const done of waiting.values()) done(null);
          waiting.clear();
          worker?.terminate();
          worker = null;
        };
      }
      const id = nextId++;
      waiting.set(id, resolve);
      worker.postMessage({ id, blob });
    } catch {
      resolve(null);
    }
  });
  BY_KEY.set(key, job);
  while (BY_KEY.size > 2) BY_KEY.delete(BY_KEY.keys().next().value!);
  return job;
}
